import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoicingState, Order, OrderPatient, OrderRejectStatus, OrderStatus } from 'models/order';
import { Box, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { formatCurrency, formatDate, formatDeliveryTime, formatPatientId, formatPatientName } from 'utils/format';
import { isSupplierOrder } from 'utils/orders';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { useNavigate, Link } from 'react-router-dom';
import PatientSectionDialog from './patient-section-dialog.component';
import ChangeInternalDeliveryDateDialog from 'components/orders/order-details/change-internal-delivery-date-dialog.component';
import BoxWithHighLighting from 'components/with-hith-light-components/box-with-highlighting.component';
import InternalOrderIdDialog from './internal-order-id-dialog.component';
import TypographyWithHighLighting from 'components/with-hith-light-components/typography-with-highlighting.component';
import ChangeExternalDeliveryDateDialog from 'components/orders/order-details/change-external-delivery-date-dialog.component';
import ChangeDeliveryDateDialog from 'components/orders/order-details/change-delivery-date-dialog.component';
import ChangePanNumberDialog from './change-pan-number-dialog.component';
import DentistNoteDialog from './dentist-note-dialog.component';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { useQuery } from '@apollo/client';
import { panNumberFeatureStatusTemplate } from 'apollo/templates/pan-number';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { patientDisplayName } from 'utils/orders';

type OrderInfoSectionProps = {
    order: Order;
    currency: string;
    versionCheck: boolean;
    redirectToModifyOrder: (isResend: boolean) => void;
    isExternalTechnician: boolean;
    isDentist: boolean;
    isCustomer: boolean;
    isSupplier: boolean;
};
const OrderInfoSection: FC<OrderInfoSectionProps> = ({
    order,
    currency,
    versionCheck,
    redirectToModifyOrder,
    isExternalTechnician,
    isDentist,
    isCustomer,
    isSupplier,
}: OrderInfoSectionProps) => {
    const { t: translate } = useTranslation(['orders', 'common', 'patient', 'users']);
    const navigate = useNavigate();
    const currentOrganization = currentOrgVar();
    const currentUser = currentUserVar();
    const isRestrictedExternalUser = permissions.isRestrictedExternalUser(currentUser?.role);
    // DD-3468: only admins may enter the client directory; other internal technicians get a dialog for the note.
    // External technicians get neither: saveLabDentistNote requires UPDATE_ORGANIZATION, which they lack.
    const canOpenClientDirectory = permissions.isTechnicianAdmin(currentUser?.role);
    const canEditDentistNote = !canOpenClientDirectory && permissions.isInternalTechnician(currentUser?.role);
    const [patientDialogOpen, setPatientDialogOpen] = useState(false);
    const [internalOrderIdDialogOpen, setInternalOrderIdDialogOpen] = useState(false);
    const [changeDeliveryDateDialogOpen, setChangeDeliveryDateDialogOpen] = useState(false);
    const [changeInternalDeliveryDateDialogOpen, setChangeInternalDeliveryDateDialogOpen] = useState(false);
    const [changeExternalDeliveryDateDialogOpen, setChangeExternalDeliveryDateDialogOpen] = useState(false);
    const [changePanNumberDialogOpen, setChangePanNumberDialogOpen] = useState(false);
    const [dentistNoteDialogOpen, setDentistNoteDialogOpen] = useState(false);
    const [panNumberEnabled, setPanNumberEnabled] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    useQuery(panNumberFeatureStatusTemplate, {
        skip: !isSupplier,
        onCompleted: ({ currentLab }) => {
            setPanNumberEnabled(currentLab?.panNumberEnabled ?? false);
        },
        onError(error) {
            showBoundary(error);
        },
    });
    const isCertificationEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.CERTIFICATION,
    );
    const isExternalLabUsersEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.EXTERNAL_LAB_USERS,
    );

    const modifyLocked = () => {
        return order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY;
    };

    const navigateToPatientHistoryPage = () => {
        if (!order.patient) {
            return;
        }
        navigate(`/patients/${order.patient.personalIdentityNumber}`);
    };

    const handleCloseDialog = () => {
        setPatientDialogOpen(false);
        setChangeDeliveryDateDialogOpen(false);
        setChangeInternalDeliveryDateDialogOpen(false);
        setInternalOrderIdDialogOpen(false);
        setChangeExternalDeliveryDateDialogOpen(false);
        setChangePanNumberDialogOpen(false);
        setDentistNoteDialogOpen(false);
    };

    const hasPatientName = (patient: OrderPatient | undefined | null) =>
        Boolean(patient?.firstName || patient?.lastName);

    const patientFullname = (patient: OrderPatient | undefined) => {
        return formatPatientName(patient, translate('orders:NO_PATIENT_REFERENCE'));
    };

    return (
        <Fragment>
            {!isRestrictedExternalUser ? (
                <Box mb={2}>
                    <Box mb={1} display="flex" alignItems="flex-end">
                        <Typography variant="h4">{translate('orders:PATIENT')}</Typography>
                        {versionCheck &&
                        !modifyLocked() &&
                        !isExternalTechnician &&
                        !order.isWarrantyOrder &&
                        (isCustomer || order.isCreatedByLab) ? (
                            <Box>
                                <Tooltip title={translate('orders:EDIT_PATIENT').toString()}>
                                    <IconButton
                                        onClick={() => setPatientDialogOpen(true)}
                                        size="small"
                                        color="primary"
                                        data-test="order-info-section.edit-patient-btn"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ) : undefined}
                        {order.patient && !order.patient.noPersonalIdentityNumber ? (
                            <Box>
                                <Tooltip title={translate('orders:SHOW_PATIENT_HISTORY').toString()}>
                                    <IconButton
                                        onClick={navigateToPatientHistoryPage}
                                        size="small"
                                        color="primary"
                                        data-test="order-info-section.patient-history-btn"
                                    >
                                        <AssignmentIndIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ) : undefined}
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">{translate('common:NAME')}</Typography>
                            {hasPatientName(order.patient) ? (
                                <TypographyWithHighLighting
                                    originalData={patientFullname(order.originalOrder.patient)}
                                    data={patientFullname(order.patient)}
                                />
                            ) : (
                                // DD-3494: a withheld patient reads like a missing PIN — small grey text.
                                <Typography component="span" variant="body2" color="#666">
                                    {translate('orders:NO_PATIENT_REFERENCE')}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4} className="mouseflow-mask">
                            <Typography variant="subtitle2">{translate('common:PERSONAL_IDENTITY_NUMBER')}</Typography>
                            {order.patient?.personalIdentityNumber ? (
                                <BoxWithHighLighting
                                    originalData={formatPatientId(order.originalOrder.patient)}
                                    data={order.patient.personalIdentityNumber}
                                />
                            ) : (
                                <Typography component="span" variant="body2" color="#666">
                                    {translate('orders:NO_PIN_NUMBER')}
                                </Typography>
                            )}
                        </Grid>
                        {order.patient?.category ? (
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2">{translate('orders:PATIENT_CATEGORY')}</Typography>
                                <BoxWithHighLighting
                                    originalData={translate(
                                        `patient:${order.originalOrder.patient?.category ?? order.patient.category}`,
                                    )}
                                    data={translate(`patient:${order.patient.category}`)}
                                />
                            </Grid>
                        ) : undefined}
                        {order.patient?.gender ? (
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2">{translate('patient:GENDER')}</Typography>
                                <BoxWithHighLighting
                                    originalData={translate(
                                        `patient:${order.originalOrder.patient?.gender ?? order.patient.gender}`,
                                    )}
                                    data={translate(`patient:${order.patient.gender}`)}
                                />
                            </Grid>
                        ) : undefined}
                    </Grid>
                </Box>
            ) : undefined}

            <Divider sx={{ marginBottom: 2, borderColor: '#eee' }} />

            <Box mb={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2">{translate('common:ORDER_ID')}</Typography>
                        <Box>{order.orderId}</Box>
                    </Grid>
                    {isSupplier ? (
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">{translate('orders:INTERNAL_ORDER_ID')}</Typography>
                            <Box>
                                {order.internalOrderId ? order.internalOrderId : undefined}
                                {!order.internalOrderId ? (
                                    <Typography component="span" variant="body2" color="#666">
                                        {translate('orders:NO_INTERNAL_ORDER_ID')}
                                    </Typography>
                                ) : undefined}
                                {!isExternalTechnician && versionCheck && !modifyLocked() ? (
                                    <Tooltip title={translate('orders:EDIT_INTERNAL_ORDER_ID').toString()}>
                                        <IconButton
                                            onClick={() => setInternalOrderIdDialogOpen(true)}
                                            size="small"
                                            color="primary"
                                            sx={{ padding: 0, marginLeft: 0.5 }}
                                            data-test="order-info-section.edit-internal-order-id-btn"
                                        >
                                            <EditIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                ) : undefined}
                            </Box>
                        </Grid>
                    ) : undefined}
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2">{translate('common:STATUS')}</Typography>
                        <Box>{translate(`orders:${order.status.toUpperCase()}`)}</Box>
                    </Grid>
                    {!isRestrictedExternalUser && (order.deliveryDetails.price || order.deliveryDetails.price === 0) ? (
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">{translate('common:PRICE')}</Typography>
                            <BoxWithHighLighting
                                originalData={formatCurrency(order.originalOrder.deliveryDetails.price, currency)}
                                data={formatCurrency(order.deliveryDetails.price, currency)}
                            />
                            {order.totalOriginalPrice && order.totalOriginalPrice !== order.deliveryDetails.price ? (
                                <Box mt={1}>
                                    <Typography variant="subtitle2">{translate('common:ORIGINAL_PRICE')}</Typography>
                                    <Box
                                        sx={{
                                            textDecoration: 'line-through',
                                        }}
                                    >
                                        {formatCurrency(order.totalOriginalPrice as number, currency)}
                                    </Box>
                                </Box>
                            ) : undefined}
                        </Grid>
                    ) : undefined}
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2">{translate('orders:ORDER_CREATED')}</Typography>
                        <Box>{formatDate(order.originalCreationDate, 'datetime')}</Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2">{translate('orders:DELIVERY_DATE')}</Typography>
                        <Box display="flex" alignItems="flex-start">
                            <BoxWithHighLighting
                                originalData={formatDate(new Date(order.originalOrder.deliveryDetails.date), 'date')}
                                data={formatDate(new Date(order.deliveryDetails.date), 'date')}
                            />
                            {order.deliveryDetails.time?.hour ? (
                                <Box ml={1}>{formatDeliveryTime(order.deliveryDetails.time)}</Box>
                            ) : undefined}
                            {versionCheck &&
                            !modifyLocked() &&
                            !isExternalTechnician &&
                            (!order.isCreatedByLab || !isCustomer) ? (
                                <Tooltip title={translate('orders:EDIT_DELIVERY_DATE').toString()}>
                                    <IconButton
                                        onClick={() => setChangeDeliveryDateDialogOpen(true)}
                                        size="small"
                                        color="primary"
                                        sx={{ padding: 0, marginLeft: 0.5 }}
                                        data-test="order-info-section.edit-delivery-date-btn"
                                    >
                                        <EditIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            ) : undefined}
                        </Box>
                    </Grid>
                    {isSupplier && !isExternalTechnician ? (
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">{translate('orders:INTERNAL_DELIVERY_DATE')}</Typography>
                            <Box display="flex" alignItems="flex-start">
                                {order.deliveryDetails.internalDeliveryDate ? (
                                    <Box mr={0.5}>
                                        {formatDate(new Date(order.deliveryDetails.internalDeliveryDate), 'date')}
                                    </Box>
                                ) : (
                                    <Box mr={0.5}>{translate('orders:DATE_UNKNOWN')}</Box>
                                )}
                                {versionCheck &&
                                !modifyLocked() &&
                                (order.status.toUpperCase() === OrderStatus.ACCEPTED_BY_LAB ||
                                    order.status.toUpperCase() === OrderStatus.SENT_BY_CUSTOMER) &&
                                order.rejectStatus?.toUpperCase() !== OrderRejectStatus.CANCELLED &&
                                order.rejectStatus?.toUpperCase() !== OrderRejectStatus.DECLINED ? (
                                    <Tooltip title={translate('orders:EDIT_INTERNAL_DELIVERY_DATE').toString()}>
                                        <IconButton
                                            onClick={() => setChangeInternalDeliveryDateDialogOpen(true)}
                                            size="small"
                                            color="primary"
                                            sx={{ padding: 0 }}
                                            data-test="order-info-section.edit-internal-delivery-date-btn"
                                        >
                                            <EditIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                ) : undefined}
                            </Box>
                        </Grid>
                    ) : undefined}
                    {isSupplier ? (
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">{translate('orders:EXTERNAL_DELIVERY_DATE')}</Typography>
                            <Box display="flex" alignItems="flex-start">
                                {order.deliveryDetails.externalDeliveryDate ? (
                                    <Box mr={0.5}>
                                        {formatDate(new Date(order.deliveryDetails.externalDeliveryDate), 'date')}
                                    </Box>
                                ) : (
                                    <Box mr={0.5}>{translate('orders:DATE_UNKNOWN')}</Box>
                                )}
                                {versionCheck &&
                                isExternalLabUsersEnabled &&
                                !isExternalTechnician &&
                                !modifyLocked() &&
                                (order.status.toUpperCase() === OrderStatus.ACCEPTED_BY_LAB ||
                                    order.status.toUpperCase() === OrderStatus.SENT_BY_CUSTOMER) &&
                                order.rejectStatus?.toUpperCase() !== OrderRejectStatus.CANCELLED &&
                                order.rejectStatus?.toUpperCase() !== OrderRejectStatus.DECLINED ? (
                                    <Tooltip title={translate('delivery-schedule:CHANGE_EXTERNAL_DEADLINE').toString()}>
                                        <IconButton
                                            onClick={() => setChangeExternalDeliveryDateDialogOpen(true)}
                                            size="small"
                                            color="primary"
                                            sx={{ padding: 0 }}
                                            data-test="order-info-section.edit-external-delivery-date-btn"
                                        >
                                            <EditIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                ) : undefined}
                            </Box>
                        </Grid>
                    ) : undefined}
                    {order.isCertified && order.certified?.by && isDentist && isCertificationEnabled && (
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">{translate('orders:CERTIFIED_BY')}</Typography>
                            <Box>
                                {order.certified.by.fullName}
                                {order.certified.on ? `(${formatDate(new Date(order.certified.on), 'date')})` : ''}
                            </Box>
                        </Grid>
                    )}
                    {isSupplier && (order.panNumber || panNumberEnabled) && (
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">{translate('orders:PAN_NUMBER')}</Typography>
                            <Box display="flex" alignItems="center">
                                <Box mr={0.5}>
                                    {order.panNumber ? (
                                        <>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <Box>{order.panNumber}</Box>
                                                {panNumberEnabled && versionCheck && !isExternalTechnician && (
                                                    <Tooltip title={translate('orders:EDIT_PAN_NUMBER').toString()}>
                                                        <IconButton
                                                            onClick={() => setChangePanNumberDialogOpen(true)}
                                                            size="small"
                                                            color="primary"
                                                            sx={{ padding: 0 }}
                                                            data-test="order-info-section.edit-pan-number-btn"
                                                        >
                                                            <EditIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            {order.panNumberDescription && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {order.panNumberDescription}
                                                </Typography>
                                            )}
                                        </>
                                    ) : (
                                        <Typography component="span" variant="body2" color="#666">
                                            {translate('orders:NOT_SET')}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Divider sx={{ marginBottom: 2, borderColor: '#eee' }} />

            <Box>
                <Grid container spacing={2}>
                    {(isSupplierOrder(order) || !isRestrictedExternalUser) && (
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">{translate('common:CUSTOMER')}</Typography>
                            <Box display="flex" alignItems="flex-start">
                                <Box mr={0.25}>{order.customer?.name}</Box>

                                {isSupplier && order?.customer?._id && (
                                    <Tooltip title={translate('orders:GO_TO_CUSTOMER').toString()}>
                                        <IconButton
                                            component={Link}
                                            to={`/client-directory/clients/${order?.customer?._id}/client-details#contactDetails`}
                                            target="_blank"
                                            sx={{ padding: 0 }}
                                            size="small"
                                            color="primary"
                                            data-test="order-info-section.go-to-customer-btn"
                                        >
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <>
                                {order.customer?._id !== order.deliveryDetails?.destination?._id ? (
                                    <Box>
                                        {order.deliveryDetails.destination.name}
                                        {isSupplier && order?.deliveryDetails?.destination?._id && (
                                            <Tooltip title={translate('orders:GO_TO_CUSTOMER').toString()}>
                                                <IconButton
                                                    component={Link}
                                                    to={`/client-directory/clients/${order?.deliveryDetails?.destination?._id}/client-details#contactDetails`}
                                                    target="_blank"
                                                    sx={{ padding: 0 }}
                                                    size="small"
                                                    color="primary"
                                                    data-test="order-info-section.go-to-destination-btn"
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                ) : undefined}
                            </>
                        </Grid>
                    )}
                    {!isSupplierOrder(order) && !isRestrictedExternalUser && (
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">{translate('common:DENTIST')}</Typography>
                            <Box display="flex" alignItems="flex-start">
                                <BoxWithHighLighting
                                    originalData={order.originalOrder.dentist?.fullName as string}
                                    data={order.dentist?.fullName as string}
                                    mr={0.25}
                                />
                                {versionCheck &&
                                !modifyLocked() &&
                                !isExternalTechnician &&
                                (!order.isCreatedByLab || !isCustomer) ? (
                                    <Tooltip title={translate('orders:EDIT_DENTIST').toString()}>
                                        <IconButton
                                            onClick={() => redirectToModifyOrder(false)}
                                            sx={{ padding: 0 }}
                                            size="small"
                                            color="primary"
                                            data-test="order-info-section.edit-dentist-btn"
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : undefined}
                                {isSupplier && order?.dentist?._id && canOpenClientDirectory && (
                                    <Tooltip title={translate('orders:GO_TO_DENTIST').toString()}>
                                        <IconButton
                                            component={Link}
                                            to={`/client-directory/dentists/${order?.dentist?._id}/dentist-details${
                                                order?.customer?._id ? `?clinicId=${order.customer._id}` : ''
                                            }`}
                                            target="_blank"
                                            sx={{ padding: 0 }}
                                            size="small"
                                            color="primary"
                                            data-test="order-info-section.go-to-dentist-btn"
                                        >
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {isSupplier && order?.dentist?._id && canEditDentistNote && (
                                    <Tooltip title={translate('users:INTERNAL_DENTIST_NOTE').toString()}>
                                        <IconButton
                                            onClick={() => setDentistNoteDialogOpen(true)}
                                            sx={{ padding: 0 }}
                                            size="small"
                                            color="primary"
                                            data-test="order-info-section.dentist-note-btn"
                                        >
                                            <EditNoteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Grid>
                    )}
                    {isSupplierOrder(order) && !isRestrictedExternalUser && (
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">{translate('orders:RESPONSIBLE_USER')}</Typography>
                            <Box display="flex" alignItems="flex-start">
                                <BoxWithHighLighting
                                    originalData={order.originalOrder.dentist?.fullName as string}
                                    data={order.dentist?.fullName as string}
                                    mr={0.25}
                                />
                            </Box>
                        </Grid>
                    )}
                    {!isRestrictedExternalUser && (
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2">{translate('orders:ORDER_CREATED_BY')}</Typography>
                            <Box display="flex" alignItems="flex-start">
                                <Box mr={0.25}>
                                    {order.created?.by?.name.firstName} {order.created?.by?.name.lastName}
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>

            <PatientSectionDialog open={patientDialogOpen} onClose={handleCloseDialog} order={order} />
            <InternalOrderIdDialog open={internalOrderIdDialogOpen} onClose={handleCloseDialog} order={order} />
            <ChangeDeliveryDateDialog open={changeDeliveryDateDialogOpen} onClose={handleCloseDialog} order={order} />
            <ChangeInternalDeliveryDateDialog
                open={changeInternalDeliveryDateDialogOpen}
                onClose={handleCloseDialog}
                order={order}
            />
            <ChangeExternalDeliveryDateDialog
                open={changeExternalDeliveryDateDialogOpen}
                onClose={handleCloseDialog}
                order={order}
            />
            {changePanNumberDialogOpen && (
                <ChangePanNumberDialog open={changePanNumberDialogOpen} onClose={handleCloseDialog} order={order} />
            )}
            {dentistNoteDialogOpen && order?.dentist?._id && order?.customer?._id && (
                <DentistNoteDialog
                    open={dentistNoteDialogOpen}
                    onClose={handleCloseDialog}
                    dentistId={order.dentist._id}
                    clinicId={order.customer._id}
                    clinicName={order.customer.name}
                />
            )}
        </Fragment>
    );
};

export default OrderInfoSection;
